import { GUIDES } from "./guides";
import { CITY_SERVICES } from "./city-services";

/**
 * Which in-depth guides belong to which service. Keys are matched against a
 * parent service slug AND against a city page's service label (lowercased),
 * so both the hub and its city cluster link into the same guide.
 */
const GUIDE_TOPICS: Array<{ guide: string; match: RegExp }> = [
  { guide: "house-washing-cost-albany", match: /house wash|exterior house|siding|vinyl/i },
  { guide: "siding-cleaning-clifton-park", match: /siding|vinyl|house wash/i },
  { guide: "roof-cleaning-cost-albany", match: /roof/i },
  { guide: "black-streaks-on-roof-albany", match: /roof/i },
  { guide: "deck-cleaning-cost-albany", match: /deck|patio/i },
  { guide: "soft-wash-vs-pressure-wash", match: /soft wash|roof|house wash/i },
  { guide: "gutter-guard-cost-albany", match: /gutter guard/i },
  { guide: "fall-gutter-cleaning-checklist-albany", match: /gutter/i },
  { guide: "winter-ice-dams-gutter-guards-albany", match: /gutter guard|gutter|roof/i },
  { guide: "freeze-thaw-driveway-damage-capital-region", match: /concrete|driveway|sidewalk|seal/i },
  { guide: "spring-exterior-cleaning-checklist-albany", match: /house wash|pressure washing|soft wash|concrete|driveway/i },
  { guide: "how-often-pressure-wash-upstate-ny", match: /pressure washing/i },
  { guide: "pressure-washing-cost-albany", match: /pressure washing|power washing|concrete|driveway|house wash/i },
  { guide: "paver-sealing-cost-albany", match: /paver|hardscape|walkway|patio|sand.?seal/i },
  { guide: "when-to-seal-pavers-capital-region", match: /paver|hardscape|sand.?seal/i },
  { guide: "polymeric-sand-vs-regular-sand", match: /paver|hardscape|sand.?seal|walkway/i },
];

export type ServiceGuideLink = { slug: string; title: string; description: string };

/** Up to `limit` guides relevant to a service slug and/or display name. */
export function getGuidesForService(
  serviceSlug: string,
  serviceName: string,
  limit = 3,
): ServiceGuideLink[] {
  const haystack = `${serviceSlug.replace(/-/g, " ")} ${serviceName}`;
  const slugs = GUIDE_TOPICS.filter((t) => t.match.test(haystack)).map((t) => t.guide);
  const seen = new Set<string>();
  const out: ServiceGuideLink[] = [];
  for (const slug of slugs) {
    if (seen.has(slug)) continue;
    seen.add(slug);
    const g = GUIDES.find((x) => x.slug === slug);
    if (g) out.push({ slug: g.slug, title: g.title, description: g.description });
    if (out.length >= limit) break;
  }
  return out;
}

export type GuideCityLink = { slug: string; service: string; city: string };

/**
 * Reverse of `getGuidesForService`: the localized city pages that belong to a
 * guide's topic, so guide readers get pushed down into the city cluster.
 * One page per town, spread across services matching the guide.
 */
export function getCitiesForGuide(guideSlug: string, limit = 8): GuideCityLink[] {
  const topics = GUIDE_TOPICS.filter((t) => t.guide === guideSlug);
  if (topics.length === 0) return [];
  const seenCity = new Set<string>();
  const out: GuideCityLink[] = [];
  for (const c of CITY_SERVICES) {
    if (seenCity.has(c.city)) continue;
    if (!topics.some((t) => t.match.test(c.service))) continue;
    seenCity.add(c.city);
    out.push({ slug: c.slug, service: c.service, city: c.city });
    if (out.length >= limit) break;
  }
  return out;
}
